/**
 * UserAvatar — avatar rond. `variant="photo"` (défaut depuis le 17/10/2026, règle
 * « jamais d'initiales quand une photo existe ») : vignette photo si disponible,
 * sinon initiales colorées via `avatarColorForSeed`. `variant="initials"` force les initiales.
 * Nom complet au clic (popover) et au survol.
 *
 * États :
 *   • `loading`            → cercle neutre #26262c (annuaire pas encore chargé) ;
 *   • user introuvable / supprimé → « ? » (réservé aux comptes supprimés) ;
 *   • `user` résolu        → initiales + couleur stable de l'utilisateur.
 */
import AvatarNamePopover from "@/components/ui/AvatarNamePopover";
import { avatarColorForSeed } from "@/lib/avatarColors";

function _initials(name) {
  return (name || "")
    .split(/\s+/).filter(Boolean).slice(0, 2)
    .map((w) => w[0].toUpperCase()).join("");
}

export default function UserAvatar({
  user,
  size = 24,
  subtitle,
  showPopover = true,
  className = "",
  testid,
  loading = false,
  variant = "photo",
  initialsStyle = null,
}) {
  if (loading) {
    return (
      <span
        aria-hidden="true"
        className={`inline-block rounded-full shrink-0 ${className}`}
        style={{ width: size, height: size, background: "#26262c" }}
        data-testid={testid ? `${testid}-loading` : undefined}
      />
    );
  }
  const resolved = user && !user.is_deleted && (user.first_name || user.last_name || user.email);
  const fullName = resolved
    ? `${user.first_name || ""} ${user.last_name || ""}`.trim() || user.email
    : user?.is_deleted ? `${`${user.first_name || ""} ${user.last_name || ""}`.trim() || "Compte"} (supprimé)` : "Compte supprimé";
  const seed = resolved ? (user.id || user.email) : null;
  const pal = seed ? avatarColorForSeed(seed) : null;
  // iter370 — règle « initiales » : texte blanc gras sur la couleur de la personne (Documents : Créé le / Modifs / Clôturé / validations ; Tâches : Créé / Assigné(s)).
  const initialsOnly = variant === "initials";

  // iter399 pt 1 — initiales uniquement : la photo d'un utilisateur n'est affichée que par ProfilePhoto (icône de profil).
  const inner = (

    <span
      className={`inline-flex items-center justify-center rounded-full font-semibold ${className}`}
      style={{
        width: size, height: size,
        background: pal?.bg || "#3B3B3B",
        color: initialsOnly ? "#fff" : (pal?.fg || "#EDEDED"),
        fontWeight: initialsOnly ? 700 : undefined,
        fontSize: Math.max(9, Math.round(size * 0.38)),
        letterSpacing: 0.2,
        ...(initialsStyle || {}),
      }}
      title={fullName}
      data-testid={testid}
    >
      {resolved ? (_initials(fullName) || "?") : "?"}
    </span>
  );

  if (!showPopover || !user) return inner;

  return (
    <AvatarNamePopover name={fullName} subtitle={subtitle} testid={testid}>
      {inner}
    </AvatarNamePopover>
  );
}
